import type { CyclePregnancyStatusRecord } from "@src/packages/homepage/pregnancy/useCyclePregnancyStatus";
import type { paths } from "@src/packages/openapi/schema";
import { mergeOutcomeNotes } from "./outcomeNotes";

type OutcomeCreateBody = NonNullable<
  paths["/{tenantId}/cycles/{cycleId}/outcomes"]["post"]["requestBody"]
>["content"]["application/json"];

type OutcomeCorrectionBody = NonNullable<
  paths["/{tenantId}/cycles/{cycleId}/outcomes/{outcomeId}/correct"]["post"]["requestBody"]
>["content"]["application/json"];

/**
 * Why a repeat recording supersedes the record already on the cycle.
 *
 * Stored as provenance on the successor row, never rendered to the patient, so
 * it stays one stable phrase rather than a translated string that would differ
 * per locale in the clinical record.
 */
export const PATIENT_OUTCOME_REPEAT_CORRECTION_REASON =
  "Patient recorded an updated result for this cycle";

/**
 * The two ways a patient's result can reach the API, and the only two the API
 * offers: author the cycle's first outcome record, or supersede the record that
 * is already current.
 */
export type PatientOutcomeWrite =
  | { kind: "create"; body: OutcomeCreateBody }
  | { kind: "correct"; outcomeId: string; body: OutcomeCorrectionBody };

export type PatientOutcomeWriteInput = {
  /**
   * The outcome record currently on the cycle, when there is one. Its presence
   * is what makes this a correction: a second `create` would leave the cycle
   * with two current records, and the API's singular `outcome` view resolves a
   * cycle with two current records to `null` — so every read surface would stop
   * seeing any result at all.
   */
  outcomeId?: string | null;
  type: OutcomeCreateBody["type"];
  resultStatus: OutcomeCreateBody["resultStatus"];
  sourceKind?: OutcomeCreateBody["sourceKind"];
  sourceRecordId?: OutcomeCreateBody["sourceRecordId"];
  sourceDocumentId?: OutcomeCreateBody["sourceDocumentId"];
  /**
   * Stable for the same content aimed at the same record, so a lost response
   * cannot be replayed into a duplicate.
   */
  clientRequestId?: string | null;
  verificationStatus?: OutcomeCreateBody["verificationStatus"];
  procedureId?: OutcomeCreateBody["procedureId"];
  procedureType?: OutcomeCreateBody["procedureType"];
  procedureModality?: OutcomeCreateBody["procedureModality"];
  procedureDate?: OutcomeCreateBody["procedureDate"];
  procedureTime?: OutcomeCreateBody["procedureTime"];
  pregnancyOutcome?: OutcomeCreateBody["pregnancyOutcome"];
  miscarriageOutcome?: OutcomeCreateBody["miscarriageOutcome"];
  biochemicalPregnancyObserved?: OutcomeCreateBody["biochemicalPregnancyObserved"];
  /**
   * How far along, and how many babies. Both endpoints have always accepted
   * these; no write path filled them until a patient could say where a pregnancy
   * stands, so they were sent as null and nothing ever read them.
   *
   * They follow the same rule as the subtypes on a correction — sent when this
   * recording carries one, omitted when it does not, so a week she gave earlier
   * survives a later answer that says nothing about it.
   */
  weekCount?: OutcomeCreateBody["weekCount"];
  liveBirthCount?: OutcomeCreateBody["liveBirthCount"];
  notes?: string | null;
  /**
   * What the record being superseded already stores. The beta hCG value lives
   * in the note ledger, so a correction that replaced `notes` outright would
   * erase the earlier value from the only row the read surfaces look at.
   */
  existingNotes?: string | null;
  /** The date this result was entered, as a local `YYYY-MM-DD` string. */
  recordedOn: string;
};

/** A request id together with the record it was minted to write. */
export type OutcomeWriteRequestId = {
  target: string;
  requestId: string;
};

/**
 * Which record a write is aimed at — the cycle's first, or a specific existing
 * one it supersedes.
 *
 * Deliberately *not* keyed on the value or the document. A retry that carries a
 * corrected number is still aimed at the same record, and it has to reuse the
 * same request id: if it minted a new one and the earlier attempt had in fact
 * committed before its response was lost, the server would have no way to
 * recognise the replay and would store a second record — leaving the cycle with
 * two current records and therefore no readable outcome at all.
 *
 * Reusing it does *not* guarantee a 409, which this previously claimed. The API's
 * replay guard compares provenance — result status, source kind, the linked row
 * — and not the recorded value, so a corrected retry matches on every field it
 * checks and is answered with the record already stored, as a success. That is
 * what `outcomeWriteWasNotApplied` exists to catch.
 */
export function outcomeWriteTarget(input: {
  outcomeId?: string | null;
}): string {
  return input.outcomeId ?? "first-record";
}

export function pregnancyStatusWriteTarget(
  head: Pick<CyclePregnancyStatusRecord, "id"> | null,
): string {
  return head?.id ?? "first-status";
}

/**
 * Keep one request id per targeted record.
 *
 * Retrying the same target reuses the id, so a response lost to a timeout can
 * still be recognised as a replay. Once the target changes — a first record that
 * turned out to already exist, and so has become a correction — the id has to be
 * minted again: the API rejects a correction whose id was already spent on
 * another write.
 */
export function resolveOutcomeWriteRequestId(
  retained: OutcomeWriteRequestId | null,
  target: string,
  createRequestId: () => string,
): OutcomeWriteRequestId {
  if (retained?.target === target) return retained;
  return { target, requestId: createRequestId() };
}

/**
 * Whether the record the API answered with is not the one this write asked for.
 *
 * A replayed request id can be answered with the record already stored instead
 * of the one just sent, because the replay guard compares result status, source
 * kind and the linked row but never the recorded value. A lost response followed
 * by a retry with a corrected number matches on all of those, so the API returns
 * the earlier record — and reports success. Without this check the screen would
 * tell the patient a value was saved that never landed.
 *
 * Everything the write *stated* is compared, not the notes alone. The notes carry
 * the beta value and so catch the common case, but a retry that changed only a
 * subtype leaves them identical and would be read as applied. Comparing the
 * stated fields closes the class rather than one field of it.
 *
 * Only the fields the write stated: a correction omits a subtype on purpose so
 * the superseded record's value survives, and treating that omission as a
 * mismatch would report every preserving correction as unapplied. So the question
 * is "did everything I asserted come back", never "does the record match my whole
 * payload".
 *
 * Sound because the API spreads what it is given onto the row — `{...input}` on a
 * create, `{...predecessor, ...input}` on a correction — so a field that landed
 * comes back as it went out. The result status and the source fields are left out
 * deliberately: the API's own replay guard already compares those and answers a
 * mismatch with a real conflict.
 */
const REPLAY_COMPARED_FIELDS = [
  "notes",
  "type",
  "pregnancyOutcome",
  "miscarriageOutcome",
  "biochemicalPregnancyObserved",
  // Stateable since a patient could say how far along she is, so they belong to
  // the same rule: a value this write asserted and did not get back means the
  // write did not land, whatever the API answered.
  "weekCount",
  "liveBirthCount",
] as const;

type OutcomeWriteComparison = Partial<
  Record<
    (typeof REPLAY_COMPARED_FIELDS)[number],
    string | number | boolean | null | undefined
  >
>;

export function outcomeWriteWasNotApplied(
  sent: OutcomeWriteComparison,
  stored: OutcomeWriteComparison,
): boolean {
  return REPLAY_COMPARED_FIELDS.some(
    (field) =>
      field in sent && (sent[field] ?? null) !== (stored[field] ?? null),
  );
}

/**
 * How a cycle ended, when the patient has already said and a beta value is being
 * recorded against it.
 *
 * `RecordBetaResultModal` does not ask how the cycle ended — it *derives* a type
 * from the hCG number: negative reads as `no_pregnancy`, borderline as nothing
 * yet, anything else as `pregnancy`. The past-cycle dialog, by contrast, asked
 * outright, and its answer can carry detail a number cannot imply: a live birth,
 * or a pregnancy that ended in loss.
 *
 * So the stated answer wins for the ending and its detail, and the number decides
 * only what the number can decide — the result status, the linked test result, the
 * note. Taking the derived type instead discarded the answer: a cycle the patient
 * said ended in loss was recorded as an ongoing pregnancy, and a live birth was
 * dropped entirely.
 *
 * Nothing is invented. With no stated ending this returns exactly what the number
 * derived, which is every recording that does not begin in that dialog.
 */
export type BetaWritableEnding = "pregnancy" | "no_pregnancy" | "miscarriage";

/**
 * The stated ending, when a beta recording can express it.
 *
 * A past cycle can also end as `cancelled` or `other`, and the dialog does not
 * offer beta details for either — the step list routes both away from the
 * hand-off. So those are not endings this write can carry, and narrowing here
 * keeps that a type-level fact rather than a cast.
 */
export function betaWritableEnding(
  type: string | null | undefined,
): BetaWritableEnding | undefined {
  return type === "pregnancy" ||
    type === "no_pregnancy" ||
    type === "miscarriage"
    ? type
    : undefined;
}

export function endingForBetaWrite(
  stated:
    | {
        type?: string | null;
        pregnancyOutcome?: OutcomeCreateBody["pregnancyOutcome"];
        miscarriageOutcome?: OutcomeCreateBody["miscarriageOutcome"];
      }
    | null
    | undefined,
  derived: {
    type: BetaWritableEnding | null;
    pregnancyOutcome?: OutcomeCreateBody["pregnancyOutcome"];
    miscarriageOutcome?: OutcomeCreateBody["miscarriageOutcome"];
  },
): {
  type: BetaWritableEnding | null;
  pregnancyOutcome: OutcomeCreateBody["pregnancyOutcome"];
  miscarriageOutcome: OutcomeCreateBody["miscarriageOutcome"];
} {
  return {
    type: betaWritableEnding(stated?.type) ?? derived.type,
    pregnancyOutcome:
      stated?.pregnancyOutcome ?? derived.pregnancyOutcome ?? null,
    miscarriageOutcome:
      stated?.miscarriageOutcome ?? derived.miscarriageOutcome ?? null,
  };
}

/**
 * The result each subtype belongs to. A subtype on any other result is a
 * contradiction: a `no_pregnancy` record cannot carry a live birth, and a
 * `pregnancy` cannot carry a miscarriage.
 */
const SUBTYPE_OWNING_RESULT = {
  pregnancyOutcome: "pregnancy",
  miscarriageOutcome: "miscarriage",
} as const;

/** The results this module knows. Anything else is treated as unrecognised. */
const KNOWN_OUTCOME_RESULTS: ReadonlySet<string> = new Set([
  "pregnancy",
  "no_pregnancy",
  "miscarriage",
  "cancelled",
  "other",
]);

/**
 * Whether a correction has to send an explicit `null` to clear a subtype.
 *
 * A correction replaces the whole record and the API fills anything omitted from
 * the record being superseded, so omitting a subtype *preserves* it — which is
 * deliberate, and is what stops a repeat beta wiping a birth recorded later. The
 * cost is that "clear this" and "leave this alone" look identical on the wire, so
 * a pregnancy corrected to a non-pregnancy kept its live birth and the record
 * asserted both at once.
 *
 * Clearing therefore needs a *positive* contradiction: a result this module
 * recognises, which is not the one the subtype belongs to. An absent or
 * unrecognised result preserves, because the beta modal really does send
 * `type: null` on two of its paths and uncertainty must not wipe recorded detail.
 */
export function correctionClearsSubtype(
  field: keyof typeof SUBTYPE_OWNING_RESULT,
  newResult: string | null | undefined,
): boolean {
  if (!newResult || !KNOWN_OUTCOME_RESULTS.has(newResult)) return false;
  return newResult !== SUBTYPE_OWNING_RESULT[field];
}

/**
 * Whether a record was authored by a clinic, and so is genuinely not the
 * patient's to change.
 *
 * An allowlist, because the absence of provenance is not evidence of a clinic.
 * `insertLegacyOutcomeSchema` omits `sourceKind` entirely, so every ending saved
 * through the cycle-intake path — which is how "Add past cycle" records one —
 * carries `null`. The correction endpoint then refuses the patient, because it
 * accepts only a `patient_entered` predecessor, and reading that refusal as a
 * clinic record told a patient their clinic had entered something they had just
 * entered themselves.
 */
export function isClinicAuthoredOutcome(
  sourceKind: string | null | undefined,
): boolean {
  return sourceKind === "provider_entered" || sourceKind === "clinic_imported";
}

/**
 * Whether to record an ending that was handed to the beta modal but never
 * written, once that modal closes.
 *
 * Keyed on whether an outcome write was *attempted*, not on whether one
 * succeeded. A write whose response was lost has still committed, and nothing
 * client-side can tell that apart from a write that failed: the mutation's own
 * refresh only runs for a conflict, so the cycle still reads as having no
 * outcome. Recording the ending then would add a *second* current record, and a
 * cycle with two resolves to no outcome at all — the failure this whole change
 * exists to prevent, and one no later recording can repair.
 *
 * So an attempt closes the door. The patient keeps the modal open on failure and
 * can retry, where the reused request id is recognised; abandoning after an
 * attempt leaves the ending unrecorded, which is the behaviour that predates this
 * recovery rather than something worse than it.
 */
export function shouldRecordAbandonedEnding(state: {
  /** True once a write has been issued, whatever its outcome. */
  outcomeWriteAttempted: boolean;
  /** Whether the entry dialog handed over an ending at all. */
  hasHandedOverEnding: boolean;
  /** Whether the cycle is already known to carry an outcome. */
  cycleAlreadyHasOutcome: boolean;
}): boolean {
  if (state.outcomeWriteAttempted) return false;
  if (!state.hasHandedOverEnding) return false;
  return !state.cycleAlreadyHasOutcome;
}

export class MissingOutcomeCorrectionKeyError extends Error {
  constructor() {
    super("An outcome correction needs a client request id");
    this.name = "MissingOutcomeCorrectionKeyError";
  }
}

/**
 * A rejected outcome write, carrying what the screen has to tell apart.
 *
 * Two outcomes mean something specific to the patient and are recoverable, so
 * they cannot collapse into the generic "something went wrong": a conflict means
 * an earlier attempt was already stored, and a refused *correction* means the
 * record on the cycle is not one the patient authored.
 *
 * The write's kind is carried because 403 alone does not identify that case.
 * `assertOutcomeSourceAuthority` refuses a create for reasons that have nothing
 * to do with who authored the record — a provenance the actor may not claim, a
 * verification they may not assert, or an actor the endpoint does not accept at
 * all, which is what a partner recording on a cycle they support receives. Only
 * the correction endpoint refuses because the predecessor is someone else's, so
 * only a correction may be reported that way.
 */
export class CycleOutcomeWriteError extends Error {
  constructor(
    message: string,
    readonly status: number,
    readonly writeKind: PatientOutcomeWrite["kind"] | null = null,
  ) {
    super(message);
    this.name = "CycleOutcomeWriteError";
  }

  /** An earlier attempt already stored a record this one did not know about. */
  get isConflict(): boolean {
    return this.status === 409;
  }

  /** Refused, for any of the reasons the endpoints refuse a write. */
  get isRefused(): boolean {
    return this.status === 403;
  }

  /**
   * Refused *because the record is not the patient's to change* — the one
   * refusal that can be attributed to a clinic having recorded the result.
   */
  get isForeignRecord(): boolean {
    return this.status === 403 && this.writeKind === "correct";
  }
}

/**
 * Decide whether a patient's result creates the cycle's outcome record or
 * supersedes the one already there, and build the body for that call.
 *
 * Recording is reachable more than once by design — "Update result" exists so a
 * borderline or first positive beta can be followed by a repeat — and both
 * writes carry `patient_entered` provenance, which is exactly what lets the
 * second one go through the correction endpoint.
 */
export function buildPatientOutcomeWrite(
  input: PatientOutcomeWriteInput,
): PatientOutcomeWrite {
  const shared = {
    type: input.type,
    resultStatus: input.resultStatus,
    sourceKind: input.sourceKind ?? "patient_entered",
    dateRecorded: input.recordedOn,
    resultDate: input.recordedOn,
    resultDatePrecision: "day" as const,
    verificationStatus: input.verificationStatus ?? "unverified",
  };

  /**
   * What the result is *attached to*: the typed test-result row holding the beta
   * value, the document she uploaded, and which procedure it belongs to.
   *
   * Sent as explicit nulls on a create, where there is nothing to preserve. On a
   * correction they follow the same rule as the subtypes — sent when this write
   * carries one, omitted when it does not, so the superseded record's value
   * survives.
   *
   * Omission matters here more than anywhere. A write that does not mention the
   * beta row is not saying there isn't one; it is saying it has nothing to add
   * about it. Nulling them meant an update that only said how far along she was
   * cut the outcome loose from her beta result and her uploaded report — while
   * `betaTestResult.ts` promises that link survives. Every one of these is
   * optional on both endpoints, so omitting them is a contract-supported way of
   * saying nothing.
   */
  const attachment = {
    sourceRecordId: input.sourceRecordId,
    sourceDocumentId: input.sourceDocumentId,
    procedureId: input.procedureId,
    procedureType: input.procedureType,
    procedureModality: input.procedureModality,
    procedureDate: input.procedureDate,
    procedureTime: input.procedureTime,
  };
  const attachmentOnCreate = {
    sourceRecordId: attachment.sourceRecordId ?? null,
    sourceDocumentId: attachment.sourceDocumentId ?? null,
    procedureId: attachment.procedureId ?? null,
    procedureType: attachment.procedureType ?? null,
    procedureModality: attachment.procedureModality ?? null,
    procedureDate: attachment.procedureDate ?? null,
    procedureTime: attachment.procedureTime ?? null,
  };
  const attachmentOnCorrection = Object.fromEntries(
    Object.entries(attachment).filter(([, value]) => value != null),
  );

  /**
   * The clinical detail a recording only sometimes carries. Sent as an explicit
   * null when the record is being created, because there is nothing to preserve;
   * omitted from a correction when the recording has nothing to say, because the
   * API keeps the superseded record's value for whatever a correction leaves out
   * and sending null would quietly drop a subtype the patient recorded earlier.
   */

  if (!input.outcomeId) {
    return {
      kind: "create",
      body: {
        ...shared,
        ...attachmentOnCreate,
        pregnancyOutcome: input.pregnancyOutcome ?? null,
        miscarriageOutcome: input.miscarriageOutcome ?? null,
        biochemicalPregnancyObserved:
          input.biochemicalPregnancyObserved ?? null,
        weekCount: input.weekCount ?? null,
        liveBirthCount: input.liveBirthCount ?? null,
        verificationNotes: null,
        ...(input.clientRequestId
          ? { clientRequestId: input.clientRequestId }
          : {}),
        notes: input.notes || null,
      } satisfies OutcomeCreateBody,
    };
  }

  /**
   * Three outcomes per subtype on a correction: send the value it carries, send
   * an explicit null when the new result contradicts it, or omit it so the
   * superseded record's value survives. See `correctionClearsSubtype`.
   *
   * `biochemicalPregnancyObserved` is only ever sent or omitted. No write path
   * sets it, so which result it belongs to is not something this module can read
   * off the code, and guessing would be a clinical claim rather than a fix.
   */
  const optionalDetail = {
    ...(input.pregnancyOutcome != null
      ? { pregnancyOutcome: input.pregnancyOutcome }
      : correctionClearsSubtype("pregnancyOutcome", input.type)
        ? { pregnancyOutcome: null }
        : {}),
    ...(input.miscarriageOutcome != null
      ? { miscarriageOutcome: input.miscarriageOutcome }
      : correctionClearsSubtype("miscarriageOutcome", input.type)
        ? { miscarriageOutcome: null }
        : {}),
    ...(input.biochemicalPregnancyObserved != null
      ? { biochemicalPregnancyObserved: input.biochemicalPregnancyObserved }
      : {}),
    ...(input.weekCount != null ? { weekCount: input.weekCount } : {}),
    ...(input.liveBirthCount != null
      ? { liveBirthCount: input.liveBirthCount }
      : {}),
  };

  // The correction endpoint makes the key mandatory: it is how a replayed
  // request is recognised as this correction rather than a different one.
  if (!input.clientRequestId) throw new MissingOutcomeCorrectionKeyError();

  return {
    kind: "correct",
    outcomeId: input.outcomeId,
    body: {
      ...shared,
      ...attachmentOnCorrection,
      ...optionalDetail,
      clientRequestId: input.clientRequestId,
      correctionReason: PATIENT_OUTCOME_REPEAT_CORRECTION_REASON,
      notes: mergeOutcomeNotes({
        existing: input.existingNotes,
        added: input.notes,
      }),
    } satisfies OutcomeCorrectionBody,
  };
}

/**
 * The ending a beta write may assert, given what the cycle already records.
 *
 * A state the patient reported is final. Once she has said a pregnancy ended — or
 * that a baby was born — a lab value cannot undo it, however that value reads. The
 * only way to change the state is to change it deliberately, in the cycle.
 *
 * Without this, a final falling hCG after a loss was read as a pregnancy: the
 * derived ending replaced hers, `correctionClearsSubtype` cleared the kind of loss
 * as a contradiction, and the preserved week count then described an ongoing
 * pregnancy at the week she miscarried. Home began describing her first scan again
 * — the exact harm the reporting step exists to prevent.
 *
 * The value itself is never rejected. It still reaches the typed test-result row
 * and the note ledger; only the *ending* is protected. A first positive, a
 * negative, and a repeat beta on a pregnancy that has not concluded are all
 * untouched, because none of those has a reported conclusion to defend.
 */
export function endingAfterBetaWrite(
  recorded:
    | {
        type?: string | null;
        pregnancyOutcome?: OutcomeCreateBody["pregnancyOutcome"];
        miscarriageOutcome?: OutcomeCreateBody["miscarriageOutcome"];
      }
    | null
    | undefined,
  proposed: {
    type: BetaWritableEnding | null;
    pregnancyOutcome?: OutcomeCreateBody["pregnancyOutcome"];
    miscarriageOutcome?: OutcomeCreateBody["miscarriageOutcome"];
  },
): {
  type: BetaWritableEnding | null;
  pregnancyOutcome: OutcomeCreateBody["pregnancyOutcome"];
  miscarriageOutcome: OutcomeCreateBody["miscarriageOutcome"];
} {
  const concluded =
    recorded?.type === "miscarriage" ||
    !!recorded?.miscarriageOutcome ||
    !!recorded?.pregnancyOutcome;

  if (concluded) {
    return {
      type: betaWritableEnding(recorded?.type) ?? "miscarriage",
      pregnancyOutcome: recorded?.pregnancyOutcome ?? null,
      miscarriageOutcome: recorded?.miscarriageOutcome ?? null,
    };
  }

  return {
    type: proposed.type,
    pregnancyOutcome: proposed.pregnancyOutcome ?? null,
    miscarriageOutcome: proposed.miscarriageOutcome ?? null,
  };
}
